import { useState } from 'react';
import { toast } from '@cencosud-cencommerce/paris-uikit';

import useBuyNowCart from '@/Application/Selectors/buyNow/useBuyNowCart';
import { store } from '@/Infrastructure/State';
import { selectShippingMethod } from '@/modules/buy-now/infrastructure/BuyNowReduxRepository';
import { setTabActive } from '@/modules/buy-now/infrastructure/slices';
import { type Cart, EShippingType } from '@/modules/cart/domain/Cart';
import type {
  DeliveryGroup,
  PickupOption,
  ShippingItem,
  ShippingMethod,
  ShippingOption
} from '@/modules/shippingMethod/domain/ShippingMethod';
import type {
  GeneralCollectOptionDto,
  GeneralCollectShippingOptionDto,
  GeneralDeliveryShippingOptionDto,
  HttpShippingMethodDraft,
  HttpShippingMethodDto
} from '@/modules/shippingMethod/infrastructure/dto/HttpShippingMethodDto';
import type { TabActive } from '@/Pages/BuyNowPage/PaymentAndShipping/interfaces';
import { getOtherGroups } from '@/sections/shippingMethods/mappers/shippingMethodMapper';

import useGetShippingGrouping from './useGetShippingGrouping';

const useSetBuyNowShippingMethods = () => {
  const { data: cart } = useBuyNowCart();
  const { results, shippingMethods } = useGetShippingGrouping();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const findOptionSelected = (
    item: ShippingItem,
    params?: {
      deliveryDateFrom?: string;
      deliveryDateTo?: string;
      shippingMethod?: string;
      shippingType?: string;
      shippingSubType?: string;
    }
  ): {
    unmappedOption:
      | GeneralDeliveryShippingOptionDto
      | GeneralCollectShippingOptionDto
      | undefined;
    mappedOption: PickupOption | ShippingOption | undefined;
  } => {
    const group = shippingMethods?.deliveryGroups?.find(
      (group) => group?.id === item?.deliveryGroupId
    );

    if (params) {
      const mappedOption = group?.shippingOptions?.find(
        (opt) =>
          opt.shippingMethod === params.shippingMethod &&
          opt.shippingType === params.shippingType &&
          opt.shippingSubType === params.shippingSubType
      );

      const deliveryGroup = results?.deliveryGroups?.find(
        (group) => group.id === item.deliveryGroupId
      );

      const option = deliveryGroup?.shippingOptions?.find(
        (opt) =>
          opt.shippingMethod === params.shippingMethod &&
          opt.shippingType === params.shippingType &&
          opt.shippingSubType === params.shippingSubType
      );

      return { unmappedOption: option, mappedOption: mappedOption };
    }

    const mappedOption = item?.isPickup
      ? group?.pickupOptions?.find((opt) => opt.id === item.id)
      : group?.shippingOptions?.find((opt) => opt.id === item.id);

    const deliveryGroup = results?.deliveryGroups?.find(
      (group) => group.id === item.deliveryGroupId
    );
    let option;
    if (deliveryGroup && mappedOption) {
      option = deliveryGroup?.shippingOptions?.find(
        (opt) =>
          opt.shippingMethod === mappedOption.shippingMethod &&
          opt.shippingType === mappedOption.shippingType &&
          opt.shippingSubType === mappedOption.shippingSubType
      );
    }

    return { unmappedOption: option, mappedOption: mappedOption };
  };

  const setDefaultShippingOptions = async (deliveryGroups: DeliveryGroup[]) => {
    try {
      const shippingMethodPayload: HttpShippingMethodDraft[] = deliveryGroups
        .map((group) => {
          if (group?.intangible) return;

          const deliveryGroup = results?.deliveryGroups?.find(
            (resultGroup) => resultGroup.id === group.id
          );
          const options = deliveryGroup?.shippingOptions;
          const selectedOption = options?.find((option) => option.selected);
          const shippingType =
            selectedOption?.shippingType as HttpShippingMethodDraft['shippingType'];
          const shippingMethod =
            selectedOption?.shippingMethod as HttpShippingMethodDraft['shippingMethod'];

          const isDelivery = shippingType === 'DELIVERY';

          const firstOption = selectedOption
            ?.options?.[0] as GeneralCollectOptionDto;

          const originNodeType = isDelivery
            ? selectedOption?.originNodeType
            : firstOption?.originNodeType;

          const originNodeCode = isDelivery
            ? selectedOption?.originNodeCode
            : firstOption?.originNodeCode;

          const option = firstOption;

          return {
            deliveryGroupId: group?.id,
            shippingType: shippingType,
            shippingSubType: selectedOption?.shippingSubType,
            shippingMethod: shippingMethod,
            option: {
              originNodeCode,
              originNodeType,
              shipDate: option?.shipDate as string,
              collectNodeCode:
                shippingType === 'COLLECT'
                  ? option?.collectNodeCode
                  : undefined,
              timeSlot: isDelivery ? option?.timeSlots?.[0] : undefined
            }
          };
        })
        .filter((item) => item !== undefined);

      const result = await store.dispatch(
        selectShippingMethod({
          shippingMethod: shippingMethodPayload,
          cartId: cart?.id ?? ''
        })
      );

      if (selectShippingMethod.fulfilled.match(result)) {
        const selectedIsCollect =
          result?.payload?.deliveryInfo?.deliveryGroups?.some(
            (group) =>
              group.shippingOption.shippingType === EShippingType.COLLECT
          );

        const selectedTab: TabActive = selectedIsCollect
          ? 'retiro'
          : 'despacho';

        store.dispatch(setTabActive(selectedTab));
      }

      if (selectShippingMethod.rejected.match(result)) {
        toast({
          title: 'Error al realizar el cambio',
          body: 'Por favor, intenta de nuevo.',
          position: 'bottom-left',
          status: 'error'
        });
        throw new Error('Error trying to select shipping method');
      }
    } catch (error) {
      handleSetError('Error trying to select shipping method');
    }
  };

  const setShippingOption = async (
    item: ShippingItem,
    scheduledParams?: {
      deliveryDateFrom?: string;
      deliveryDateTo?: string;
      shippingMethod?: string;
      shippingType?: string;
      shippingSubType?: string;
    }
  ) => {
    try {
      setIsLoading(true);

      const selectedOptions = getOtherGroups(
        results as HttpShippingMethodDto,
        shippingMethods as ShippingMethod,
        item.deliveryGroupId as string,
        cart as Cart
      );

      const { unmappedOption, mappedOption } = findOptionSelected(
        item,
        scheduledParams
      );
      if (unmappedOption && item.deliveryGroupId) {
        const { options } = unmappedOption;

        let selectedDateOption = options[0];
        if (scheduledParams?.deliveryDateFrom) {
          const foundOption = options.find(
            (opt) =>
              opt.deliveryDateFrom?.split('T')[0] ===
              scheduledParams.deliveryDateFrom
          );
          if (foundOption) {
            selectedDateOption = foundOption;
          }
        }

        const optionTimeSlot = selectedDateOption.timeSlots
          ? selectedDateOption.timeSlots[0]
          : undefined;

        const payload: HttpShippingMethodDraft = {
          deliveryGroupId: item?.deliveryGroupId,
          shippingType: unmappedOption?.shippingType,
          shippingSubType: unmappedOption?.shippingSubType,
          shippingMethod: unmappedOption?.shippingMethod,
          option: {
            shipDate:
              selectedDateOption?.shipDate ?? mappedOption?.shipDate ?? '',
            originNodeType:
              unmappedOption?.originNodeType ??
              mappedOption?.originNodeType ??
              'PROVIDER',
            originNodeCode:
              unmappedOption?.originNodeCode ??
              mappedOption?.originNodeCode ??
              ''
          }
        };

        if (!item.isPickup) {
          payload.option.timeSlot = optionTimeSlot;
          selectedOptions.unshift(payload);
          const result = await store.dispatch(
            selectShippingMethod({
              shippingMethod: selectedOptions,
              cartId: cart?.id ?? ''
            })
          );
          if (selectShippingMethod.rejected.match(result)) {
            setIsLoading(false);
            setError('Error trying to select shipping method');
            return false;
          }
        } else {
          const collectOption = mappedOption as PickupOption;
          payload.option.collectNodeCode = collectOption?.collectNodeCode;
          selectedOptions.unshift(payload);
          const result = await store.dispatch(
            selectShippingMethod({
              shippingMethod: selectedOptions,
              cartId: cart?.id ?? ''
            })
          );
          if (selectShippingMethod.rejected.match(result)) {
            setIsLoading(false);
            setError('Error trying to select shipping method');
            return false;
          }
        }
      }
      setIsLoading(false);
      return true;
    } catch (error) {
      handleSetError('Error trying to select shipping method');
    }
  };

  const handleSetError = (value: string | undefined) => {
    setError(value);
  };

  return {
    setShippingOption,
    setDefaultShippingOptions,
    isLoading,
    error,
    handleSetError
  };
};

export default useSetBuyNowShippingMethods;
